import React, { useEffect, useState, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { createPortal } from 'react-dom';
import { ArrowLeft, FileText, Download, Calendar, LayoutTemplate, Save, Check, ChevronDown, ChevronUp, Settings2, AlignLeft, Eye, X, BarChart3 } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Checkbox } from '../components/ui/checkbox';
import { Separator } from '../components/ui/separator';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { motion, AnimatePresence } from 'framer-motion';
import { clients, sentimentOverTime, coverageByTier, topSources, spokespersonMentions } from '../lib/data';
import { AreaChart, Area, XAxis, YAxis, CartesianGrid, ResponsiveContainer, Tooltip } from 'recharts';
const TEMPLATES = [{
  id: 'executive',
  name: 'Executive Summary',
  description: 'Sintesi rapida per il top management. Solo i dati essenziali.',
  sectionsCount: 3,
  sections: {
    summary: true,
    kpi: true,
    topArticles: true,
    sentiment: false,
    tier: false,
    sources: false,
    spokespersons: false,
    allArticles: false
  },
  preview: <div className="flex flex-col gap-1.5 p-2 w-full h-full bg-muted/20 rounded-md border border-border/50" data-id="element-1125">
        <div className="h-2 w-1/3 bg-accent/40 rounded-sm mb-1" data-id="element-1126" />
        <div className="h-6 w-full bg-accent/20 rounded-sm" data-id="element-1127" />
        <div className="grid grid-cols-2 gap-1.5" data-id="element-1128">
          <div className="h-8 bg-muted rounded-sm" data-id="element-1129" />
          <div className="h-8 bg-muted rounded-sm" data-id="element-1130" />
        </div>
        <div className="h-10 w-full bg-muted rounded-sm mt-auto" data-id="element-1131" />
      </div>
}, {
  id: 'standard',
  name: 'Report Standard',
  description: 'Il formato più utilizzato. Include analisi qualitativa e quantitativa.',
  sectionsCount: 5,
  sections: {
    summary: true,
    kpi: true,
    topArticles: true,
    sentiment: true,
    tier: true,
    sources: false,
    spokespersons: false,
    allArticles: false
  },
  preview: <div className="flex flex-col gap-1.5 p-2 w-full h-full bg-muted/20 rounded-md border border-border/50" data-id="element-1132">
        <div className="h-2 w-1/3 bg-accent/40 rounded-sm mb-1" data-id="element-1133" />
        <div className="h-6 w-full bg-accent/20 rounded-sm" data-id="element-1134" />
        <div className="grid grid-cols-2 gap-1.5" data-id="element-1135">
          <div className="h-8 bg-muted rounded-sm" data-id="element-1136" />
          <div className="h-8 bg-muted rounded-sm" data-id="element-1137" />
        </div>
        <div className="grid grid-cols-2 gap-1.5 mt-auto" data-id="element-1138">
          <div className="h-6 bg-accent/10 rounded-sm" data-id="element-1139" />
          <div className="h-6 bg-accent/10 rounded-sm" data-id="element-1140" />
        </div>
      </div>
}, {
  id: 'full',
  name: 'Analisi Completa',
  description: 'Tutti i dati disponibili, inclusa la lista completa degli articoli.',
  sectionsCount: 8,
  sections: {
    summary: true,
    kpi: true,
    topArticles: true,
    sentiment: true,
    tier: true,
    sources: true,
    spokespersons: true,
    allArticles: true
  },
  preview: <div className="flex flex-col gap-1.5 p-2 w-full h-full bg-muted/20 rounded-md border border-border/50" data-id="element-1141">
        <div className="h-2 w-1/3 bg-accent/40 rounded-sm mb-1" data-id="element-1142" />
        <div className="h-4 w-full bg-accent/20 rounded-sm" data-id="element-1143" />
        <div className="grid grid-cols-3 gap-1" data-id="element-1144">
          <div className="h-6 bg-muted rounded-sm" data-id="element-1145" />
        </div>
        <div className="grid grid-cols-2 gap-1.5" data-id="element-1146">
          <div className="h-6 bg-accent/10 rounded-sm" data-id="element-1147" />
          <div className="h-6 bg-accent/10 rounded-sm" data-id="element-1148" />
        </div>
        <div className="h-8 w-full bg-muted/50 rounded-sm mt-auto" data-id="element-1149" />
      </div>
}];
const AVAILABLE_SECTIONS = [{
  id: 'summary',
  label: 'Riepilogo AI (Executive Summary)'
}, {
  id: 'kpi',
  label: 'KPI Principali'
}, {
  id: 'topArticles',
  label: 'Top Articoli'
}, {
  id: 'sentiment',
  label: 'Grafico Sentiment'
}, {
  id: 'tier',
  label: 'Copertura per Tier'
}, {
  id: 'sources',
  label: 'Top Testate'
}, {
  id: 'spokespersons',
  label: 'Portavoce citati'
}, {
  id: 'allArticles',
  label: 'Lista completa articoli'
}];
export function ReportBuilder() {
  const navigate = useNavigate();
  const [reportName, setReportName] = useState('Report Mensile Aprile 2026');
  const [template, setTemplate] = useState('standard');
  const [sections, setSections] = useState(TEMPLATES[1].sections);
  const [customizeOpen, setCustomizeOpen] = useState(false);
  const [notesOpen, setNotesOpen] = useState(false);
  const [notes, setNotes] = useState('');
  const [previewOpen, setPreviewOpen] = useState(false);
  const [startDate, setStartDate] = useState('2025-04-01');
  const [endDate, setEndDate] = useState('2025-04-30');
  const intrumClient = clients.find(c => c.id === 'intrum') || clients[0];
  useEffect(() => {
    if (previewOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'unset';
    }
    return () => {
      document.body.style.overflow = 'unset';
    };
  }, [previewOpen]);
  const handleTemplateChange = (templateId: string) => {
    setTemplate(templateId);
    const selectedTemplate = TEMPLATES.find(t => t.id === templateId);
    if (selectedTemplate) {
      setSections(selectedTemplate.sections);
    }
  };
  const toggleSection = (key: keyof typeof sections) => {
    setSections(prev => {
      const newSections = {
        ...prev,
        [key]: !prev[key]
      };
      // Check if it matches any template exactly
      const matchedTemplate = TEMPLATES.find(t => Object.entries(t.sections).every(([k, v]) => newSections[k as keyof typeof sections] === v));
      if (matchedTemplate) {
        setTemplate(matchedTemplate.id);
      } else {
        setTemplate('custom');
      }
      return newSections;
    });
  };
  const selectedCount = Object.values(sections).filter(Boolean).length;
  return <div className="h-full flex flex-col bg-background relative" data-id="element-1150">
      {/* Header */}
      <div className="bg-card border-b border-border" data-id="element-1151">
        <div className="px-6 pt-6 md:pt-8 max-w-[1400px] mx-auto w-full" data-id="element-1152">
          <button onClick={() => navigate('/reports')} className="flex items-center gap-2 text-sm text-muted-foreground hover:text-foreground mb-4 transition-colors" data-id="element-1153">
            <ArrowLeft className="h-3.5 w-3.5" data-id="element-1154" />
            Report
          </button>

          <div className="flex flex-col md:flex-row md:items-start justify-between gap-4 mb-6" data-id="element-1155">
            <div className="min-w-0 flex-1" data-id="element-1156">
              <input type="text" value={reportName} onChange={e => setReportName(e.target.value)} className="text-2xl md:text-3xl font-normal tracking-tight leading-tight mb-2 bg-transparent border-none focus:outline-none focus:ring-0 p-0 m-0 w-full truncate hover:bg-muted/30 px-2 -ml-2 rounded transition-colors cursor-text" data-id="element-1157" />
              <div className="text-sm text-muted-foreground font-normal px-2 -ml-2" data-id="element-1158">
                Configurazione nuovo report
              </div>
            </div>

            <div className="flex items-center gap-2.5 flex-shrink-0" data-id="element-1159">
              <div className="flex items-center gap-2 text-sm bg-muted/30 px-3 py-2 rounded-lg border border-border" data-id="element-1160">
                <Calendar className="h-4 w-4 text-muted-foreground flex-shrink-0" data-id="element-1161" />
                <label className="text-muted-foreground text-xs font-medium whitespace-nowrap" data-id="element-1162">
                  Da
                </label>
                <input type="date" value={startDate} onChange={e => setStartDate(e.target.value)} className="bg-transparent border-none outline-none text-sm text-foreground font-medium w-[130px] cursor-pointer" data-id="element-1163" />
                <span className="text-muted-foreground mx-1" data-id="element-1164">—</span>
                <label className="text-muted-foreground text-xs font-medium whitespace-nowrap" data-id="element-1165">
                  A
                </label>
                <input type="date" value={endDate} onChange={e => setEndDate(e.target.value)} className="bg-transparent border-none outline-none text-sm text-foreground font-medium w-[130px] cursor-pointer" data-id="element-1166" />
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="flex-1 overflow-auto pb-24" data-id="element-1167">
        <div className="max-w-[1400px] mx-auto px-6 py-6 w-full space-y-10" data-id="element-1168">
          {/* Templates Hero */}
          <section data-id="element-1169">
            <div className="mb-6" data-id="element-1170">
              <h2 className="text-lg font-normal flex items-center gap-2" data-id="element-1171">
                <LayoutTemplate className="h-5 w-5 text-accent" data-id="element-1172" />
                Scegli un template
              </h2>
              <p className="text-sm text-muted-foreground mt-1" data-id="element-1173">
                Seleziona un formato di partenza. Potrai personalizzare le
                sezioni in seguito.
              </p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-6" data-id="element-1174">
              {TEMPLATES.map(t => {
              const isSelected = template === t.id;
              return <motion.div key={t.id} whileHover={{
                scale: 1.02
              }} whileTap={{
                scale: 0.98
              }} onClick={() => handleTemplateChange(t.id)} className={`relative cursor-pointer rounded-xl border p-5 transition-all duration-200 flex flex-col h-full bg-card ${isSelected ? 'border-accent shadow-md ring-1 ring-accent/20' : 'border-border hover:border-accent/50 hover:shadow-sm'}`} data-id="element-1175">
                    {isSelected && <div className="absolute -top-3 -right-3 w-7 h-7 bg-accent text-white rounded-full flex items-center justify-center shadow-sm z-10" data-id="element-1176">
                        <Check className="h-4 w-4" data-id="element-1177" />
                      </div>}

                    <div className="h-32 mb-5 rounded-lg overflow-hidden flex-shrink-0" data-id="element-1178">
                      {t.preview}
                    </div>

                    <div className="mt-auto" data-id="element-1179">
                      <div className="flex items-center justify-between mb-2" data-id="element-1180">
                        <h3 className="font-normal text-base" data-id="element-1181">{t.name}</h3>
                        <span className="text-xs font-medium bg-muted px-2 py-1 rounded-md text-muted-foreground" data-id="element-1182">
                          {t.sectionsCount} sezioni
                        </span>
                      </div>
                      <p className="text-sm text-muted-foreground leading-relaxed" data-id="element-1183">
                        {t.description}
                      </p>
                    </div>
                  </motion.div>;
            })}
            </div>
          </section>

          <Separator data-id="element-1184" />

          {/* Customize Sections */}
          <section data-id="element-1185">
            <button onClick={() => setCustomizeOpen(!customizeOpen)} className="w-full flex items-center justify-between py-2 group" data-id="element-1186">
              <div className="flex items-center gap-2" data-id="element-1187">
                <Settings2 className="h-5 w-5 text-muted-foreground group-hover:text-foreground transition-colors" data-id="element-1188" />
                <h2 className="text-lg font-normal group-hover:text-accent transition-colors" data-id="element-1189">
                  Personalizza sezioni
                </h2>
              </div>
              {customizeOpen ? <ChevronUp className="h-5 w-5 text-muted-foreground" data-id="element-1190" /> : <ChevronDown className="h-5 w-5 text-muted-foreground" data-id="element-1191" />}
            </button>

            <AnimatePresence data-id="element-1192">
              {customizeOpen && <motion.div initial={{
              height: 0,
              opacity: 0
            }} animate={{
              height: 'auto',
              opacity: 1
            }} exit={{
              height: 0,
              opacity: 0
            }} transition={{
              duration: 0.3,
              ease: 'easeInOut'
            }} className="overflow-hidden" data-id="element-1193">
                  <div className="pt-6 pb-2 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4" data-id="element-1194">
                    {AVAILABLE_SECTIONS.map(section => {
                  const isChecked = sections[section.id as keyof typeof sections];
                  return <div key={section.id} onClick={() => toggleSection(section.id as keyof typeof sections)} className={`flex items-start gap-3 p-4 rounded-lg border cursor-pointer transition-colors ${isChecked ? 'border-accent/50 bg-accent/5' : 'border-border bg-card hover:border-accent/30'}`} data-id="element-1195">
                          <Checkbox checked={isChecked} onCheckedChange={() => toggleSection(section.id as keyof typeof sections)} className="mt-0.5" data-id="element-1196" />
                          <span className="text-sm font-medium leading-snug select-none" data-id="element-1197">
                            {section.label}
                          </span>
                        </div>;
                })}
                  </div>
                </motion.div>}
            </AnimatePresence>
          </section>

          <Separator data-id="element-1198" />

          {/* Notes Section */}
          <section data-id="element-1199">
            <button onClick={() => setNotesOpen(!notesOpen)} className="w-full flex items-center justify-between py-2 group" data-id="element-1200">
              <div className="flex items-center gap-2" data-id="element-1201">
                <AlignLeft className="h-5 w-5 text-muted-foreground group-hover:text-foreground transition-colors" data-id="element-1202" />
                <h2 className="text-lg font-normal group-hover:text-accent transition-colors" data-id="element-1203">
                  Note aggiuntive
                </h2>
              </div>
              {notesOpen ? <ChevronUp className="h-5 w-5 text-muted-foreground" data-id="element-1204" /> : <ChevronDown className="h-5 w-5 text-muted-foreground" data-id="element-1205" />}
            </button>

            <AnimatePresence data-id="element-1206">
              {notesOpen && <motion.div initial={{
              height: 0,
              opacity: 0
            }} animate={{
              height: 'auto',
              opacity: 1
            }} exit={{
              height: 0,
              opacity: 0
            }} transition={{
              duration: 0.3,
              ease: 'easeInOut'
            }} className="overflow-hidden" data-id="element-1207">
                  <div className="pt-6 pb-2" data-id="element-1208">
                    <textarea value={notes} onChange={e => setNotes(e.target.value)} placeholder="Inserisci un commento o una nota introduttiva per il cliente che apparirà all'inizio del report..." className="w-full px-4 py-3 text-sm border border-input rounded-lg bg-card focus:outline-none focus:ring-2 focus:ring-accent min-h-[120px] resize-y shadow-sm" data-id="element-1209" />
                  </div>
                </motion.div>}
            </AnimatePresence>
          </section>
        </div>
      </div>

      {/* Sticky Bottom Bar */}
      <div className="sticky bottom-0 left-0 right-0 border-t border-border bg-card/95 backdrop-blur supports-[backdrop-filter]:bg-card/80 p-4 md:px-8 z-20 shadow-[0_-4px_20px_-10px_rgba(0,0,0,0.1)]" data-id="element-1210">
        <div className="max-w-[1400px] mx-auto flex flex-col sm:flex-row items-center justify-between gap-4" data-id="element-1211">
          <div className="text-sm font-medium text-muted-foreground" data-id="element-1212">
            <span className="text-foreground font-semibold" data-id="element-1213">
              {selectedCount}
            </span>{' '}
            sezioni selezionate
          </div>

          <div className="flex items-center gap-3 w-full sm:w-auto" data-id="element-1214">
            <Button variant="outline" className="rounded-full flex-1 sm:flex-none" data-id="element-1215">
              <Save className="h-4 w-4 mr-2" data-id="element-1216" />
              Salva Bozza
            </Button>
            <Button variant="outline" className="rounded-full flex-1 sm:flex-none" data-id="element-1217">
              <Download className="h-4 w-4 mr-2" data-id="element-1218" />
              Esporta PDF
            </Button>
            <Button variant="outline" className="rounded-full flex-1 sm:flex-none" onClick={() => setPreviewOpen(true)} data-id="element-1219">
              <Eye className="h-4 w-4 mr-2" data-id="element-1220" />
              Anteprima
            </Button>
            <Button className="rounded-full flex-1 sm:flex-none px-8" data-id="element-1221">
              Genera Report
            </Button>
          </div>
        </div>
      </div>

      {/* Preview Overlay */}
      <AnimatePresence data-id="element-1222">
        {previewOpen && <motion.div key="preview-overlay" initial={{
        opacity: 0
      }} animate={{
        opacity: 1
      }} exit={{
        opacity: 0
      }} transition={{
        duration: 0.3,
        ease: 'easeInOut'
      }} className="fixed inset-0 bg-white flex flex-col" style={{
        zIndex: 99999
      }} data-id="element-1223">
            {/* Top bar - matching AI Mode style */}
            <div className="h-16 border-b border-border flex items-center justify-between px-6 flex-shrink-0 bg-white" data-id="element-1224">
              <button type="button" onClick={e => {
            e.preventDefault();
            e.stopPropagation();
            setPreviewOpen(false);
          }} className="w-10 h-10 rounded-full flex items-center justify-center cursor-pointer hover:bg-muted transition-colors bg-transparent border-none outline-none" style={{
            zIndex: 100000
          }} aria-label="Chiudi anteprima" data-id="element-1225">
                <X className="h-5 w-5 text-foreground pointer-events-none" data-id="element-1226" />
              </button>

              <div className="flex items-center gap-2" data-id="element-1227">
                <Eye className="h-4 w-4 text-accent" data-id="element-1228" />
                <span className="font-medium text-sm text-foreground" data-id="element-1229">
                  Anteprima Report
                </span>
              </div>

              <div className="flex items-center gap-3" data-id="element-1230">
                <Button variant="outline" className="rounded-full h-9 px-4 text-sm border-accent text-accent hover:bg-accent/10" data-id="element-1231">
                  <Download className="h-4 w-4 mr-2" data-id="element-1232" />
                  Esporta PDF
                </Button>
                <Button className="rounded-full h-9 px-4 text-sm" data-id="element-1233">
                  Genera Report
                </Button>
              </div>
            </div>

            {/* Document Area */}
            <div className="flex-1 overflow-auto bg-[#F0F1F3]" data-id="element-1234">
              <div className="py-10 px-6" data-id="element-1235">
                <div className="w-[794px] min-h-[1123px] bg-white mx-auto shadow-[0_2px_20px_rgba(0,0,0,0.08)] flex flex-col p-16 text-foreground rounded-sm" data-id="element-1236">
                  {/* Document Header */}
                  <div className="flex justify-between items-start mb-14" data-id="element-1237">
                    <div data-id="element-1238">
                      <div className="w-12 h-1 bg-accent mb-6 rounded-full" data-id="element-1239" />
                      <h1 className="text-[28px] font-bold mb-3 text-[#1A1F2E] leading-tight" data-id="element-1240">
                        {reportName}
                      </h1>
                      <p className="text-sm text-muted-foreground" data-id="element-1241">
                        Generato il{' '}
                        {new Date().toLocaleDateString('it-IT', {
                      day: 'numeric',
                      month: 'long',
                      year: 'numeric'
                    })}
                      </p>
                      <p className="text-sm text-muted-foreground mt-1" data-id="element-1242">
                        Esclapon & Co. — Media Intelligence
                      </p>
                    </div>
                    <img src={intrumClient.logo} alt="Logo" className="h-8 object-contain mt-2" style={{
                  filter: 'brightness(0)'
                }} data-id="element-1243" />
                  </div>

                  {/* Notes */}
                  {notes && <div className="mb-10 text-[15px] leading-relaxed text-muted-foreground italic border-l-4 border-accent pl-5 py-1" data-id="element-1244">
                      {notes}
                    </div>}

                  {/* Sections */}
                  <div className="space-y-12 flex-1" data-id="element-1245">
                    {sections.summary && <section data-id="element-1246">
                        <h2 className="text-lg font-semibold mb-4 text-[#1A1F2E] flex items-center gap-2" data-id="element-1247">
                          <div className="w-1.5 h-1.5 rounded-full bg-accent" data-id="element-1248" />
                          Executive Summary
                        </h2>
                        <div className="text-[15px] leading-relaxed text-muted-foreground space-y-3 pl-4" data-id="element-1249">
                          <p data-id="element-1250">
                            Nel periodo analizzato, Intrum ha registrato una
                            copertura mediatica significativa con 20 articoli
                            pubblicati su 12 testate nazionali e specializzate.
                            Il sentiment complessivo è prevalentemente positivo
                            (65%), con un AVE totale di €187k.
                          </p>
                          <p data-id="element-1251">
                            I temi principali riguardano le operazioni di
                            gestione dei crediti deteriorati (NPL), le strategie
                            di espansione nel mercato italiano e il
                            posizionamento competitivo nel settore del credit
                            management.
                          </p>
                        </div>
                      </section>}

                    {sections.kpi && <section data-id="element-1252">
                        <h2 className="text-lg font-semibold mb-4 text-[#1A1F2E] flex items-center gap-2" data-id="element-1253">
                          <div className="w-1.5 h-1.5 rounded-full bg-accent" data-id="element-1254" />
                          KPI Principali
                        </h2>
                        <div className="grid grid-cols-2 gap-4 pl-4" data-id="element-1255">
                          <div className="p-5 border border-border rounded-lg" data-id="element-1256">
                            <div className="text-xs uppercase tracking-wider text-muted-foreground mb-2" data-id="element-1257">
                              Articoli totali
                            </div>
                            <div className="text-3xl font-bold text-[#1A1F2E]" style={{
                        fontVariantNumeric: 'tabular-nums'
                      }} data-id="element-1258">
                              20
                            </div>
                            <div className="text-xs text-green-600 mt-1" data-id="element-1259">
                              +25% vs periodo prec.
                            </div>
                          </div>
                          <div className="p-5 border border-border rounded-lg" data-id="element-1260">
                            <div className="text-xs uppercase tracking-wider text-muted-foreground mb-2" data-id="element-1261">
                              Score medio
                            </div>
                            <div className="text-3xl font-bold text-accent" style={{
                        fontVariantNumeric: 'tabular-nums'
                      }} data-id="element-1262">
                              7.4
                            </div>
                            <div className="text-xs text-green-600 mt-1" data-id="element-1263">
                              +8.8% vs periodo prec.
                            </div>
                          </div>
                          <div className="p-5 border border-border rounded-lg" data-id="element-1264">
                            <div className="text-xs uppercase tracking-wider text-muted-foreground mb-2" data-id="element-1265">
                              AVE totale
                            </div>
                            <div className="text-3xl font-bold text-[#1A1F2E]" style={{
                        fontVariantNumeric: 'tabular-nums'
                      }} data-id="element-1266">
                              €187k
                            </div>
                            <div className="text-xs text-green-600 mt-1" data-id="element-1267">
                              +31.7% vs periodo prec.
                            </div>
                          </div>
                          <div className="p-5 border border-border rounded-lg" data-id="element-1268">
                            <div className="text-xs uppercase tracking-wider text-muted-foreground mb-2" data-id="element-1269">
                              Sentiment
                            </div>
                            <div className="flex items-baseline gap-2" data-id="element-1270">
                              <span className="text-3xl font-bold text-accent" style={{
                          fontVariantNumeric: 'tabular-nums'
                        }} data-id="element-1271">
                                65%
                              </span>
                              <span className="text-sm text-muted-foreground" data-id="element-1272">
                                positivo
                              </span>
                            </div>
                            <div className="flex gap-1 mt-2" data-id="element-1273">
                              <div className="h-1.5 rounded-full bg-accent" style={{
                          width: '65%'
                        }} data-id="element-1274" />
                              <div className="h-1.5 rounded-full bg-[#64B5F6]" style={{
                          width: '25%'
                        }} data-id="element-1275" />
                              <div className="h-1.5 rounded-full bg-[#1A3A5C]" style={{
                          width: '10%'
                        }} data-id="element-1276" />
                            </div>
                          </div>
                        </div>
                      </section>}

                    {sections.topArticles && <section data-id="element-1277">
                        <h2 className="text-lg font-semibold mb-4 text-[#1A1F2E] flex items-center gap-2" data-id="element-1278">
                          <div className="w-1.5 h-1.5 rounded-full bg-accent" data-id="element-1279" />
                          Top Articoli
                        </h2>
                        <div className="space-y-0 pl-4" data-id="element-1280">
                          {[{
                      title: 'Intrum acquisisce portafoglio NPL da €1.2 miliardi da UniCredit',
                      source: 'Il Sole 24 Ore',
                      score: 9.2,
                      date: '15 Apr'
                    }, {
                      title: 'Credit management: Intrum rafforza la presenza in Italia',
                      source: 'MF Milano Finanza',
                      score: 8.7,
                      date: '12 Apr'
                    }, {
                      title: 'Il mercato NPL italiano attira gli investitori nordeuropei',
                      source: 'Corriere della Sera',
                      score: 8.1,
                      date: '10 Apr'
                    }].map((article, i) => <div key={i} className="flex items-center justify-between py-3 border-b border-border last:border-0" data-id="element-1281">
                              <div className="flex-1 min-w-0 pr-4" data-id="element-1282">
                                <div className="text-[14px] font-medium text-[#1A1F2E] leading-snug" data-id="element-1283">
                                  {article.title}
                                </div>
                                <div className="text-xs text-muted-foreground mt-1" data-id="element-1284">
                                  {article.source} · {article.date}
                                </div>
                              </div>
                              <div className="text-sm font-semibold text-accent flex-shrink-0" style={{
                        fontVariantNumeric: 'tabular-nums'
                      }} data-id="element-1285">
                                {article.score}
                              </div>
                            </div>)}
                        </div>
                      </section>}

                    {sections.sentiment && <section data-id="element-1286">
                        <h2 className="text-lg font-semibold mb-4 text-[#1A1F2E] flex items-center gap-2" data-id="element-1287">
                          <div className="w-1.5 h-1.5 rounded-full bg-accent" data-id="element-1288" />
                          Andamento Sentiment
                        </h2>
                        <div className="ml-4" data-id="element-1289">
                          <AreaChart width={620} height={180} data={sentimentOverTime} margin={{
                      top: 5,
                      right: 0,
                      bottom: 0,
                      left: -20
                    }} data-id="element-1290">
                            <CartesianGrid strokeDasharray="3 3" stroke="#E8E8E5" vertical={false} data-id="element-1291" />
                            <XAxis dataKey="date" tick={{
                        fontSize: 10
                      }} stroke="#8B8680" axisLine={false} tickLine={false} dy={5} data-id="element-1292" />
                            <YAxis tick={{
                        fontSize: 10
                      }} stroke="#8B8680" axisLine={false} tickLine={false} data-id="element-1293" />
                            <Tooltip data-id="element-1294" />
                            <Area type="monotone" dataKey="positive" stackId="1" stroke="#0187c3" fill="#0187c3" fillOpacity={0.5} strokeWidth={1.5} data-id="element-1295" />
                            <Area type="monotone" dataKey="neutral" stackId="1" stroke="#64B5F6" fill="#64B5F6" fillOpacity={0.35} strokeWidth={1.5} data-id="element-1296" />
                            <Area type="monotone" dataKey="negative" stackId="1" stroke="#1A3A5C" fill="#1A3A5C" fillOpacity={0.5} strokeWidth={1.5} data-id="element-1297" />
                          </AreaChart>
                        </div>
                      </section>}

                    {sections.tier && <section data-id="element-1298">
                        <h2 className="text-lg font-semibold mb-4 text-[#1A1F2E] flex items-center gap-2" data-id="element-1299">
                          <div className="w-1.5 h-1.5 rounded-full bg-accent" data-id="element-1300" />
                          Copertura per Tier
                        </h2>
                        <div className="space-y-3 pl-4" data-id="element-1301">
                          {[{
                      tier: 'Tier 1',
                      count: 5,
                      pct: 25
                    }, {
                      tier: 'Tier 2',
                      count: 8,
                      pct: 40
                    }, {
                      tier: 'Tier 3',
                      count: 4,
                      pct: 20
                    }, {
                      tier: 'Specializzate',
                      count: 3,
                      pct: 15
                    }].map(item => <div key={item.tier} data-id="element-1302">
                              <div className="flex items-center justify-between mb-1.5 text-sm" data-id="element-1303">
                                <span className="font-medium text-[#1A1F2E]" data-id="element-1304">
                                  {item.tier}
                                </span>
                                <span className="text-muted-foreground" style={{
                          fontVariantNumeric: 'tabular-nums'
                        }} data-id="element-1305">
                                  {item.count} articoli ({item.pct}%)
                                </span>
                              </div>
                              <div className="h-2 bg-muted rounded-full overflow-hidden" data-id="element-1306">
                                <div className="h-full bg-accent rounded-full" style={{
                          width: `${item.pct}%`
                        }} data-id="element-1307" />
                              </div>
                            </div>)}
                        </div>
                      </section>}

                    {sections.sources && <section data-id="element-1308">
                        <h2 className="text-lg font-semibold mb-4 text-[#1A1F2E] flex items-center gap-2" data-id="element-1309">
                          <div className="w-1.5 h-1.5 rounded-full bg-accent" data-id="element-1310" />
                          Top Testate
                        </h2>
                        <div className="space-y-0 pl-4" data-id="element-1311">
                          {[{
                      name: 'Il Sole 24 Ore',
                      articles: 4,
                      score: 8.5
                    }, {
                      name: 'MF Milano Finanza',
                      articles: 3,
                      score: 8.1
                    }, {
                      name: 'Corriere della Sera',
                      articles: 2,
                      score: 7.8
                    }, {
                      name: 'La Repubblica',
                      articles: 2,
                      score: 7.2
                    }, {
                      name: 'Il Messaggero',
                      articles: 2,
                      score: 6.9
                    }].map(source => <div key={source.name} className="flex items-center justify-between py-2.5 border-b border-border last:border-0 text-sm" data-id="element-1312">
                              <span className="font-medium text-[#1A1F2E]" data-id="element-1313">
                                {source.name}
                              </span>
                              <div className="flex items-center gap-6 text-muted-foreground" data-id="element-1314">
                                <span style={{
                          fontVariantNumeric: 'tabular-nums'
                        }} data-id="element-1315">
                                  {source.articles} art.
                                </span>
                                <span className="font-semibold text-[#1A1F2E] w-8 text-right" style={{
                          fontVariantNumeric: 'tabular-nums'
                        }} data-id="element-1316">
                                  {source.score}
                                </span>
                              </div>
                            </div>)}
                        </div>
                      </section>}

                    {sections.spokespersons && <section data-id="element-1317">
                        <h2 className="text-lg font-semibold mb-4 text-[#1A1F2E] flex items-center gap-2" data-id="element-1318">
                          <div className="w-1.5 h-1.5 rounded-full bg-accent" data-id="element-1319" />
                          Portavoce citati
                        </h2>
                        <div className="grid grid-cols-2 gap-3 pl-4" data-id="element-1320">
                          {[{
                      name: 'Andrea Mangoni',
                      role: 'CEO Italia',
                      mentions: 8
                    }, {
                      name: 'Lars Eriksson',
                      role: 'Group CFO',
                      mentions: 4
                    }, {
                      name: 'Marco Bianchi',
                      role: 'Head of NPL',
                      mentions: 3
                    }, {
                      name: 'Sofia Rossi',
                      role: 'Head of Communications',
                      mentions: 2
                    }].map(person => <div key={person.name} className="p-3 border border-border rounded-lg flex items-center justify-between" data-id="element-1321">
                              <div data-id="element-1322">
                                <div className="text-sm font-medium text-[#1A1F2E]" data-id="element-1323">
                                  {person.name}
                                </div>
                                <div className="text-xs text-muted-foreground" data-id="element-1324">
                                  {person.role}
                                </div>
                              </div>
                              <div className="text-lg font-semibold text-accent" style={{
                        fontVariantNumeric: 'tabular-nums'
                      }} data-id="element-1325">
                                {person.mentions}
                              </div>
                            </div>)}
                        </div>
                      </section>}

                    {sections.allArticles && <section data-id="element-1326">
                        <h2 className="text-lg font-semibold mb-4 text-[#1A1F2E] flex items-center gap-2" data-id="element-1327">
                          <div className="w-1.5 h-1.5 rounded-full bg-accent" data-id="element-1328" />
                          Lista completa articoli
                        </h2>
                        <div className="border border-border rounded-lg overflow-hidden ml-4" data-id="element-1329">
                          <div className="bg-[#F8F9FA] px-4 py-2.5 flex gap-4 border-b border-border text-xs uppercase tracking-wider text-muted-foreground font-medium" data-id="element-1330">
                            <div className="w-20" data-id="element-1331">Data</div>
                            <div className="w-32" data-id="element-1332">Testata</div>
                            <div className="flex-1" data-id="element-1333">Titolo</div>
                            <div className="w-14 text-right" data-id="element-1334">Score</div>
                          </div>
                          {[{
                      date: '15/04',
                      source: 'Il Sole 24 Ore',
                      title: 'Intrum acquisisce portafoglio NPL da €1.2 miliardi',
                      score: '9.2'
                    }, {
                      date: '14/04',
                      source: 'MF Milano Finanza',
                      title: 'Credit management: Intrum rafforza la presenza',
                      score: '8.7'
                    }, {
                      date: '12/04',
                      source: 'Corriere della Sera',
                      title: 'Il mercato NPL italiano attira investitori',
                      score: '8.1'
                    }, {
                      date: '10/04',
                      source: 'La Repubblica',
                      title: 'Crediti deteriorati, il ruolo dei servicer nordeuropei',
                      score: '7.6'
                    }, {
                      date: '08/04',
                      source: 'Il Messaggero',
                      title: 'Intrum: piano di espansione nel Lazio',
                      score: '7.2'
                    }].map((row, i) => <div key={i} className="px-4 py-2.5 flex gap-4 border-b border-border last:border-0 text-sm" data-id="element-1335">
                              <div className="w-20 text-muted-foreground" style={{
                        fontVariantNumeric: 'tabular-nums'
                      }} data-id="element-1336">
                                {row.date}
                              </div>
                              <div className="w-32 text-muted-foreground truncate" data-id="element-1337">
                                {row.source}
                              </div>
                              <div className="flex-1 text-[#1A1F2E] font-medium truncate" data-id="element-1338">
                                {row.title}
                              </div>
                              <div className="w-14 text-right font-semibold" style={{
                        fontVariantNumeric: 'tabular-nums'
                      }} data-id="element-1339">
                                {row.score}
                              </div>
                            </div>)}
                        </div>
                      </section>}
                  </div>

                  {/* Footer */}
                  <div className="mt-16 pt-5 border-t border-border flex items-center justify-between" data-id="element-1340">
                    <div className="text-xs text-muted-foreground" data-id="element-1341">
                      Esclapon & Co. — Media Intelligence
                    </div>
                    <div className="text-xs text-muted-foreground" data-id="element-1342">
                      Report generato automaticamente ·{' '}
                      {new Date().toLocaleDateString('it-IT')}
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </motion.div>}
      </AnimatePresence>
    </div>;
}